'use client';

import clsx from 'clsx';
import { CSSProperties, FC, ReactNode } from 'react';
import { ReviewsOperationsProgressCardBinding } from '@/components/reviews-operations-progress/use-reviews-operations-progress-controller';
import { overlayLayers } from '@/ui/overlay-layers';

type OperationProgressCardProps = Omit<ReviewsOperationsProgressCardBinding, 'kind'> & {
    icon: ReactNode;
};

const AiProgressIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
        <rect x="8" y="8" width="8" height="8" rx="1.8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 10.5h3v3h-3zM4.5 9v6M19.5 9v6M9 4.5h6M9 19.5h6" />
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m18.2 4.2.7 1.4 1.5.2-1.1 1 .3 1.4-1.4-.7-1.3.7.3-1.4-1.1-1 1.4-.2z"
        />
    </svg>
);

const ExportProgressIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.5 3.5h9l4 4v13h-13z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 3.5v4h4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 15h8M8 18h5" />
    </svg>
);

const AutoReplyProgressIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5.5h14v8.5H9l-4 3.5z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 9.5h7M8.5 12h4" />
    </svg>
);

const OperationProgressCard: FC<OperationProgressCardProps> = ({
    title,
    details,
    etaLabel,
    isEtaCompleted,
    progressPercent,
    tone,
    icon,
    onClose,
}) => {
    const progressStyle = progressPercent !== null ? ({ width: `${progressPercent}%` } as CSSProperties) : undefined;

    return (
        <article className="pointer-events-auto relative rounded-lg border border-slate-200 bg-white/95 p-3 text-xs text-slate-700 shadow-sm backdrop-blur-sm">
            <button
                type="button"
                aria-label="Скрыть прогресс"
                className="absolute right-2 top-2 h-5 w-5 rounded text-[11px] leading-none text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                onClick={onClose}
            >
                x
            </button>
            <div className="flex items-center gap-2 font-semibold">
                {icon}
                <span>{title}</span>
            </div>
            <p className="mt-1 text-[11px] text-slate-600">{details}</p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                    style={progressStyle}
                    className={clsx(
                        'h-full rounded-full transition-all duration-300',
                        progressPercent === null && 'w-1/3 animate-pulse',
                        tone === 'indigo' && 'bg-indigo-500',
                        tone === 'amber' && 'bg-amber-500',
                        tone === 'emerald' && 'bg-emerald-500',
                    )}
                />
            </div>
            <p className={clsx('mt-1 text-[11px]', isEtaCompleted ? 'font-semibold text-slate-700' : 'text-slate-500')}>
                {etaLabel}
            </p>
        </article>
    );
};

const resolveIcon = (kind: ReviewsOperationsProgressCardBinding['kind']) => {
    if (kind === 'ai') {
        return <AiProgressIcon />;
    }

    if (kind === 'autoReply') {
        return <AutoReplyProgressIcon />;
    }

    return <ExportProgressIcon />;
};

type ReviewsOperationsProgressViewProps = {
    cards: ReviewsOperationsProgressCardBinding[];
};

export const ReviewsOperationsProgressView = ({ cards }: ReviewsOperationsProgressViewProps) => {
    if (!cards.length) {
        return null;
    }

    return (
        <div
            className="pointer-events-none fixed bottom-3 right-3 flex w-[min(320px,calc(100vw-1.5rem))] flex-col gap-2"
            style={{ zIndex: overlayLayers.overlayToast }}
        >
            {cards.map((card) => (
                <OperationProgressCard
                    key={card.kind}
                    title={card.title}
                    details={card.details}
                    etaLabel={card.etaLabel}
                    isEtaCompleted={card.isEtaCompleted}
                    progressPercent={card.progressPercent}
                    tone={card.tone}
                    icon={resolveIcon(card.kind)}
                    onClose={card.onClose}
                />
            ))}
        </div>
    );
};
